// Reader app — capture a printed page with the camera, POST to the worker,
// receive {text, language} back, then speak via window.speechSynthesis.

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const API_URL = import.meta.env.VITE_API_URL ?? '';

type Phase =
  | {kind: 'idle'}
  | {kind: 'preview'; dataUrl: string; mimeType: string; base64: string}
  | {kind: 'submitting'; dataUrl: string}
  | {kind: 'done'; dataUrl: string}
  | {kind: 'error'; message: string; dataUrl?: string};

async function fileToBase64(file: File): Promise<{base64: string; dataUrl: string}> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1] ?? '';
      resolve({base64, dataUrl});
    };
    reader.onerror = () => reject(new Error('file_read_failed'));
    reader.readAsDataURL(file);
  });
}

function langPrefix(code: string): string {
  return code.toLowerCase().slice(0, 2);
}

export function App() {
  const [phase, setPhase] = useState<Phase>({kind: 'idle'});
  const [text, setText] = useState('');
  const [language, setLanguage] = useState<'uk' | 'en'>('uk');
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [voiceURI, setVoiceURI] = useState<string>('');
  const [rate, setRate] = useState(1);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadVoices = () => setVoices(window.speechSynthesis.getVoices());
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const filteredVoices = useMemo(() => {
    const wanted = langPrefix(language);
    return voices.filter((v) => langPrefix(v.lang) === wanted);
  }, [voices, language]);

  useEffect(() => {
    if (filteredVoices.length === 0) {
      setVoiceURI('');
      return;
    }
    if (!filteredVoices.some((v) => v.voiceURI === voiceURI)) {
      setVoiceURI(filteredVoices[0]!.voiceURI);
    }
  }, [filteredVoices, voiceURI]);

  const onPick = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const {base64, dataUrl} = await fileToBase64(file);
      setPhase({kind: 'preview', dataUrl, mimeType: file.type || 'image/jpeg', base64});
    } catch (err) {
      setPhase({kind: 'error', message: (err as Error).message});
    }
  }, []);

  const onRetake = useCallback(() => {
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setPaused(false);
    setText('');
    setPhase({kind: 'idle'});
  }, []);

  const onSubmit = useCallback(async () => {
    if (phase.kind !== 'preview') return;
    setPhase({kind: 'submitting', dataUrl: phase.dataUrl});
    try {
      const r = await fetch(API_URL, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify({imageBase64: phase.base64, mimeType: phase.mimeType}),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({error: `http_${r.status}`}));
        throw new Error(body?.error ?? `http_${r.status}`);
      }
      const data = (await r.json()) as {text: string; language?: string};
      setText(data.text.trim());
      if (data.language === 'uk' || data.language === 'en') {
        setLanguage(data.language);
      }
      setPhase({kind: 'done', dataUrl: phase.dataUrl});
    } catch (err) {
      setPhase({kind: 'error', message: (err as Error).message, dataUrl: phase.dataUrl});
    }
  }, [phase]);

  const speak = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(trimmed);
    const chosen = voices.find((v) => v.voiceURI === voiceURI);
    if (chosen) u.voice = chosen;
    u.lang = chosen?.lang ?? language;
    u.rate = rate;
    u.onstart = () => { setSpeaking(true); setPaused(false); };
    u.onend = () => { setSpeaking(false); setPaused(false); };
    u.onerror = () => { setSpeaking(false); setPaused(false); };
    window.speechSynthesis.speak(u);
  }, [text, voices, voiceURI, language, rate]);

  const pause = useCallback(() => { window.speechSynthesis.pause(); setPaused(true); }, []);
  const resume = useCallback(() => { window.speechSynthesis.resume(); setPaused(false); }, []);
  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setPaused(false);
  }, []);

  useEffect(() => {
    if (phase.kind !== 'done') window.speechSynthesis.cancel();
  }, [phase.kind]);

  return (
    <div className="app">
      {phase.kind !== 'idle' && <h1>Reader</h1>}

      {phase.kind === 'idle' && (
        <div className="hero">
          <div className="hero-icon" aria-hidden="true">
            <svg width="72" height="72" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M22 24 L30 24 L40 16 L40 48 L30 40 L22 40 Z"
                fill="currentColor"
              />
              <path
                d="M46 24 Q50 32 46 40"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                fill="none"
              />
              <path
                d="M51 19 Q57 32 51 45"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                fill="none"
                opacity="0.55"
              />
            </svg>
          </div>
          <h1 className="hero-title">Reader</h1>
          <p className="hero-tagline">photo · words · voice</p>

          <div className="hero-actions">
            <button
              className="btn btn-primary"
              onClick={() => cameraInputRef.current?.click()}
            >
              <svg className="btn-icon" width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M7 7 L9 5 H15 L17 7 H20 A2 2 0 0 1 22 9 V18 A2 2 0 0 1 20 20 H4 A2 2 0 0 1 2 18 V9 A2 2 0 0 1 4 7 H7 Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                <circle cx="12" cy="13" r="3.8" stroke="currentColor" strokeWidth="1.8" />
              </svg>
              Take photo
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => libraryInputRef.current?.click()}
            >
              <svg className="btn-icon" width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="8.5" cy="10" r="1.5" stroke="currentColor" strokeWidth="1.8" />
                <path d="M3 17 L9 11 L13 15 L17 12 L21 16" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinejoin="round" strokeLinecap="round" />
              </svg>
              Choose from library
            </button>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onPick}
            />
            <input
              ref={libraryInputRef}
              type="file"
              accept="image/*"
              onChange={onPick}
            />
          </div>

          <div className="hero-decor" aria-hidden="true">
            <svg width="200" height="120" viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Standing books — different heights */}
              <rect x="14" y="34" width="20" height="68" rx="1" stroke="currentColor" strokeWidth="1.6" />
              <line x1="20" y1="44" x2="20" y2="92" stroke="currentColor" strokeWidth="1" />
              <line x1="28" y1="44" x2="28" y2="92" stroke="currentColor" strokeWidth="1" />

              <rect x="38" y="22" width="20" height="80" rx="1" stroke="currentColor" strokeWidth="1.6" />
              <line x1="44" y1="32" x2="44" y2="92" stroke="currentColor" strokeWidth="1" />

              <rect x="62" y="30" width="20" height="72" rx="1" stroke="currentColor" strokeWidth="1.6" />
              <line x1="68" y1="40" x2="68" y2="92" stroke="currentColor" strokeWidth="1" />
              <line x1="76" y1="40" x2="76" y2="92" stroke="currentColor" strokeWidth="1" />

              {/* Tilted book */}
              <g transform="rotate(-10 96 70)">
                <rect x="86" y="34" width="20" height="68" rx="1" stroke="currentColor" strokeWidth="1.6" />
                <line x1="92" y1="44" x2="92" y2="92" stroke="currentColor" strokeWidth="1" />
              </g>

              <rect x="116" y="26" width="20" height="76" rx="1" stroke="currentColor" strokeWidth="1.6" />
              <line x1="122" y1="36" x2="122" y2="92" stroke="currentColor" strokeWidth="1" />
              <line x1="130" y1="36" x2="130" y2="92" stroke="currentColor" strokeWidth="1" />

              {/* Stacked horizontal books on the right */}
              <rect x="148" y="86" width="42" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <line x1="156" y1="91" x2="184" y2="91" stroke="currentColor" strokeWidth="0.8" />
              <rect x="144" y="72" width="50" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <line x1="152" y1="77" x2="186" y2="77" stroke="currentColor" strokeWidth="0.8" />
              <rect x="150" y="58" width="38" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <line x1="158" y1="63" x2="184" y2="63" stroke="currentColor" strokeWidth="0.8" />

              {/* Shelf */}
              <line x1="6" y1="102" x2="194" y2="102" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
        </div>
      )}

      {phase.kind === 'preview' && (
        <div className="section">
          <img src={phase.dataUrl} alt="preview" className="preview" />
          <button className="btn btn-primary" onClick={onSubmit} style={{marginBottom: 8}}>
            Transcribe
          </button>
          <button className="btn btn-secondary" onClick={onRetake}>
            Retake
          </button>
        </div>
      )}

      {phase.kind === 'submitting' && (
        <div className="section">
          <img src={phase.dataUrl} alt="preview" className="preview" />
          <p className="status"><span className="spinner" />Transcribing...</p>
        </div>
      )}

      {phase.kind === 'done' && (
        <div className="section">
          <textarea
            className="text-area"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Transcribed text will appear here"
          />
          <div className="row">
            <label htmlFor="lang">Language</label>
            <select
              id="lang"
              value={language}
              onChange={(e) => setLanguage(e.target.value as 'uk' | 'en')}
            >
              <option value="uk">Ukrainian</option>
              <option value="en">English</option>
            </select>
          </div>
          <div className="row">
            <label htmlFor="voice">Voice</label>
            <select
              id="voice"
              value={voiceURI}
              onChange={(e) => setVoiceURI(e.target.value)}
              disabled={filteredVoices.length === 0}
            >
              {filteredVoices.length === 0 && (
                <option value="">(no voices for {language})</option>
              )}
              {filteredVoices.map((v) => (
                <option key={v.voiceURI} value={v.voiceURI}>
                  {v.name} ({v.lang})
                </option>
              ))}
            </select>
          </div>
          <div className="row">
            <label htmlFor="rate">Speed</label>
            <input
              id="rate"
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
            />
            <span className="rate-value">{rate.toFixed(1)}x</span>
          </div>
          <div className="btn-row">
            {!speaking ? (
              <button className="btn btn-primary btn-small" onClick={speak} disabled={!text.trim()}>
                ▶︎ Play
              </button>
            ) : !paused ? (
              <button className="btn btn-secondary btn-small" onClick={pause}>
                ❙❙ Pause
              </button>
            ) : (
              <button className="btn btn-primary btn-small" onClick={resume}>
                ▶︎ Resume
              </button>
            )}
            <button className="btn btn-secondary btn-small" onClick={stop} disabled={!speaking}>
              ■ Stop
            </button>
            <button className="btn btn-secondary btn-small" onClick={onRetake}>
              ↺ New
            </button>
          </div>
        </div>
      )}

      {phase.kind === 'error' && (
        <div className="section">
          {phase.dataUrl && <img src={phase.dataUrl} alt="preview" className="preview" />}
          <p className="status error">Error: {phase.message}</p>
          <button className="btn btn-primary" onClick={onRetake}>
            Try again
          </button>
        </div>
      )}
    </div>
  );
}
